import React, { useEffect, useState } from 'react';
import { getPuzzles } from '../services/api';
import { useCart } from '../context/CartContext';
import { Link, useParams, Routes, Route } from 'react-router-dom';
import api from '../services/api';
import { getPuzzleOptions } from '../services/api';

interface Puzzle {
  id: string;
  title: string;
  description: string;
  price: number;
  photos: string[];
  solutionImage?: string;
  puzzleType: string;
  availability: string;
  difficulty?: string;
  pieces?: number;
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
}

const formatPuzzleType = (type: string) => {
  const types: { [key: string]: string } = {
    'FULL_COURSE': 'Full Course',
    'SINGLE_HOLE': 'Single Hole',
    'SECTION': 'Section',
    'FRONT_NINE': 'Front Nine',
    'BACK_NINE': 'Back Nine',
    'LOGO': 'Logo'
  };
  return types[type] || type;
};

const formatAvailability = (status: string) => {
  const statuses: { [key: string]: string } = {
    'AVAILABLE': 'Available',
    'COMING_SOON': 'Coming Soon',
    'SOLD_OUT': 'Sold Out'
  };
  return statuses[status] || status;
};

const formatDifficulty = (difficulty: string) => {
  if (!difficulty) return '';
  const map: Record<string, string> = {
    'EASY': 'Easy',
    'MEDIUM': 'Medium',
    'HARD': 'Hard',
  };
  return map[difficulty] || difficulty;
};

const Puzzles: React.FC = () => {
  const [puzzles, setPuzzles] = useState<Puzzle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartMessage, setCartMessage] = useState<React.ReactNode>(null);

  useEffect(() => {
    const fetchPuzzles = async () => {
      try {
        const data = await getPuzzles();
        setPuzzles(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load puzzles. Please try again later.');
        setLoading(false);
      }
    };

    fetchPuzzles();
  }, []);

  const filteredPuzzles = puzzles.filter(puzzle => {
    const matchesSearch = (puzzle.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (puzzle.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const { addItem } = useCart();

  const handleAddToCart = (puzzle: any) => {
    addItem({
      id: puzzle.id,
      title: puzzle.title,
      price: puzzle.price,
    });

    setCartMessage(
      <span>
        Added to <Link to="/cart" className="underline">Cart!</Link>
      </span>
    );

    setTimeout(() => setCartMessage(''), 3000);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading puzzles...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4d4f' }}>{error}</div>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', color: '#2e7d32' }}>All Puzzles</h1>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <input
            type="text"
            placeholder="Search puzzles..."
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              flex: 1
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2rem' }}>
        {filteredPuzzles.map((puzzle) => (
          <div key={puzzle.id} style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
            <Link to={`/puzzles/${puzzle.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <img
                src={puzzle.photos[0]}
                alt={puzzle.title}
                style={{ width: '100%', height: '200px', objectFit: 'contain' }}
              />
              <div style={{ padding: '1.5rem' }}>
                <h2 style={{ fontSize: '1.25rem', color: '#2e7d32', marginBottom: '0.5rem' }}>{puzzle.title}</h2>
              </div>
            </Link>
            <div style={{ padding: '1.5rem', paddingTop: 0 }}>
              <p style={{ color: '#666', marginBottom: '1rem' }}>{puzzle.description}</p>
              {/* {puzzle.difficulty && (
                <div style={{ color: '#444', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>Difficulty: </span>
                  <span>{formatDifficulty(puzzle.difficulty)}</span>
                </div>
              )} */}
              {typeof puzzle.pieces === 'number' && puzzle.pieces > 0 && (
                <div style={{ color: '#444', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold' }}>Pieces: </span>
                  <span>{puzzle.pieces}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '1.25rem', color: '#f57c00' }}>${puzzle.price.toFixed(2)}</span>
                <Link
                  to={`/puzzles/${puzzle.id}`}
                  style={{
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  View Details
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
      {cartMessage && (
        <div className="fixed left-1/2 top-6 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded shadow z-50 transition-opacity text-lg font-semibold">
          {cartMessage}
        </div>
      )}
    </div>
  );
};

const PuzzleDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const [puzzle, setPuzzle] = useState<any>(null);
  const [mainImageIdx, setMainImageIdx] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cartMessage, setCartMessage] = useState<React.ReactNode>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [additionalRequests, setAdditionalRequests] = useState('');

  useEffect(() => {
    const fetchPuzzle = async () => {
      try {
        const response = await api.get(`/puzzles/${id}`);
        setPuzzle(response.data);
      } catch (err) {
        setError('Failed to load puzzle details.');
      } finally {
        setLoading(false);
      }
    };
    fetchPuzzle();
    // Fetch options
    const fetchOptions = async () => {
      setOptionsLoading(true);
      try {
        const opts = await getPuzzleOptions(id!);
        setOptions(opts);
      } catch (e) {
        setOptions([]);
      } finally {
        setOptionsLoading(false);
      }
    };
    if (id) fetchOptions();
  }, [id]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowModal(false);
    };
    if (showModal) {
      window.addEventListener('keydown', handleEsc);
    } else {
      setZoomed(false);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showModal]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading puzzle details...</div>;
  if (error) return <div style={{ textAlign: 'center', padding: '2rem', color: '#ff4d4f' }}>{error}</div>;
  if (!puzzle) return null;

  const galleryImages = [...puzzle.photos];
  if (puzzle.solutionImage) {
    galleryImages.push(puzzle.solutionImage);
  }

  const handleOptionChange = (optionId: string) => {
    setSelectedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const getTotalPrice = () => {
    let total = puzzle.price;
    for (const opt of options) {
      if (selectedOptions.includes(opt.id)) {
        total += opt.price;
      }
    }
    return total;
  };

  const handleAddToCart = (puzzle: any) => {
    addItem({
      id: puzzle.id,
      title: puzzle.title,
      price: getTotalPrice(),
      options: options.filter(opt => selectedOptions.includes(opt.id)),
      additionalRequests: additionalRequests.trim() ? additionalRequests.trim() : undefined,
    });
    setCartMessage(
      <span>
        Added to <Link to="/cart" className="underline">Cart!</Link>
      </span>
    );
    setTimeout(() => setCartMessage(''), 3000);
  };

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <Link to="/puzzles" style={{ color: '#2e7d32', textDecoration: 'none', display: 'inline-block', marginBottom: '1rem' }}>&larr; Back to Puzzles</Link>
      <h1 style={{ fontSize: '2rem', color: '#2e7d32', marginBottom: '1rem' }}>{puzzle.title}</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginBottom: '1.5rem' }}>
        <div style={{ flex: 1 }}>
          <img
            src={galleryImages[mainImageIdx]}
            alt={puzzle.title}
            style={{ 
              width: '100%', 
              height: '320px', 
              objectFit: 'contain', 
              backgroundColor: '#f5f5f5',
              borderRadius: '4px', 
              marginBottom: '1rem', 
              cursor: 'zoom-in' 
            }}
            onClick={() => setShowModal(true)}
          />
          {galleryImages.length > 1 && (
            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
              {galleryImages.map((photo: string, idx: number) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img
                    src={photo}
                    alt={`Thumbnail ${idx + 1}`}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      opacity: mainImageIdx === idx ? 1 : 0.7,
                      border: '1px solid #d9d9d9'
                    }}
                    onClick={() => setMainImageIdx(idx)}
                  />
                  {puzzle.solutionImage && idx === galleryImages.length - 1 && (
                    <span style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '4px',
                      backgroundColor: '#f57c00',
                      color: 'white',
                      fontSize: '0.75rem',
                      padding: '2px 4px',
                      borderRadius: '2px'
                    }}>Solution</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ color: '#666' }}>{puzzle.description}</p>
          <div>
            <span style={{ fontWeight: 'bold' }}>Price: </span>
            <span style={{ color: '#f57c00', fontSize: '1.25rem' }}>${puzzle.price.toFixed(2)}</span>
          </div>
          {/* <div>
            <span style={{ fontWeight: 'bold' }}>Type: </span>
            <span>{formatPuzzleType(puzzle.puzzleType)}</span>
          </div> */}
          {/* <div>
            <span style={{ fontWeight: 'bold' }}>Availability: </span>
            <span>{formatAvailability(puzzle.availability)}</span>
          </div> */}
          {/* {puzzle.difficulty && (
            <div>
              <span style={{ fontWeight: 'bold' }}>Difficulty: </span>
              <span>{formatDifficulty(puzzle.difficulty)}</span>
            </div>
          )} */}
          {typeof puzzle.pieces === 'number' && puzzle.pieces > 0 && (
            <div>
              <span style={{ fontWeight: 'bold' }}>Pieces: </span>
              <span>{puzzle.pieces}</span>
            </div>
          )}
          {(typeof puzzle.length === 'number' || typeof puzzle.width === 'number' || typeof puzzle.height === 'number') && (
            <div>
              {typeof puzzle.length === 'number' && typeof puzzle.width === 'number' && typeof puzzle.height === 'number' ? (
                <>
                  <span style={{ fontWeight: 'bold' }}>Size: </span>
                  <span>
                    {puzzle.length}mm x {puzzle.width}mm x {puzzle.height}mm (
                    {(puzzle.length / 25.4).toFixed(2)}in x {(puzzle.width / 25.4).toFixed(2)}in x {(puzzle.height / 25.4).toFixed(2)}in)
                  </span>
                </>
              ) : (
                <>
                  <span style={{ fontWeight: 'bold' }}>Size: </span>
                  {typeof puzzle.length === 'number' && `${puzzle.length} mm (L) `}
                  {typeof puzzle.width === 'number' && `${puzzle.width} mm (W) `}
                  {typeof puzzle.height === 'number' && `${puzzle.height} mm (H)`}
                </>
              )}
            </div>
          )}
          {/* {typeof puzzle.weight === 'number' && (
            <div>
              <span style={{ fontWeight: 'bold' }}>Weight: </span>
              <span>{puzzle.weight} oz</span>
            </div>
          )} */}
          {/* Customization Options */}
          {optionsLoading ? (
            <div style={{ color: '#888' }}>Loading options...</div>
          ) : options.length > 0 && (
            <div style={{ margin: '1rem 0' }}>
              <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Customization Options:</div>
              {options.map(opt => (
                <label key={opt.id} style={{ display: 'block', marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(opt.id)}
                    onChange={() => handleOptionChange(opt.id)}
                    style={{ marginRight: 8 }}
                  />
                  {opt.name} {opt.price > 0 && (<span style={{ color: '#f57c00' }}>+${opt.price.toFixed(2)}</span>)}
                </label>
              ))}
            </div>
          )}
          <div>
            <span style={{ fontWeight: 'bold' }}>Total: </span>
            <span style={{ color: '#f57c00', fontSize: '1.25rem' }}>${getTotalPrice().toFixed(2)}</span>
          </div>
          {/* Additional Requests field */}
          <div style={{ marginTop: '1rem' }}>
            <label htmlFor="additional-requests" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
              Additional Requests
            </label>
            <textarea
              id="additional-requests"
              maxLength={255}
              value={additionalRequests}
              onChange={e => setAdditionalRequests(e.target.value)}
              placeholder="Additional requests can be accommodated within reason"
              style={{ width: '100%', minHeight: 60, border: '1px solid #ccc', borderRadius: 4, padding: 8, fontSize: 14 }}
            />
            <div style={{ fontSize: 12, color: '#888', textAlign: 'right' }}>{additionalRequests.length}/255</div>
          </div>
          <button
            onClick={() => handleAddToCart(puzzle)}
            style={{
              backgroundColor: '#2e7d32',
              color: 'white',
              padding: '0.5rem 1.5rem',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1.5rem'
            }}
          >
            Add to Cart
          </button>
        </div>
      </div>
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.8)'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: '4px solid white',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: zoomed ? 'scale(1.5)' : 'scale(1)',
              transition: 'transform 0.2s',
              cursor: zoomed ? 'zoom-out' : 'zoom-in'
            }}
            onClick={e => {
              e.stopPropagation();
              setZoomed(z => !z);
            }}
          >
            <img
              src={galleryImages[mainImageIdx]}
              alt="Full Size"
              style={{ maxHeight: '80vh', maxWidth: '80vw', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
      {cartMessage && (
        <div className="fixed left-1/2 top-6 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded shadow z-50 transition-opacity text-lg font-semibold">
          {cartMessage}
        </div>
      )}
    </div>
  );
};

const PuzzlesRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<Puzzles />} />
    <Route path="/:id" element={<PuzzleDetails />} />
  </Routes>
);

export default PuzzlesRoutes; 